import { Controller } from "@hotwired/stimulus"

// Sub contract detail: invoice entry/approve (knock down + overbill confirm),
// payments (compliance-hold aware, waiver auto-request), changes, compliance
// docs + chase, portal toggle, A4 invoice audit polling.
export default class extends Controller {
  static targets = ["invoiceModal", "approveModal", "approveAmount", "approveRetainage", "approveNotes", "approveApNote", "subSignApNote", "subSignSubNote",
                    "approveWarning", "approveBtn", "paymentModal", "paymentWarning", "paymentBtn",
                    "changeModal", "complianceModal", "editModal", "filesModal", "filesList",
                    "invoiceDropZone", "invoiceFileInput", "invoiceChips", "invoiceReadStatus", "deleteModal", "pcoDeleteModal", "pcoWithdrawModal", "pcoFinishAmendModal", "pcoAmendReason", "memberAccessModal", "memberAccessSelect", "memberAccessHint", "bundleModal",
                    "invoiceDetailModal", "changeDetailModal", "invDeleteModal", "invDeleteNote",
                    "subSignModal", "subSignRows", "subSignRowTemplate", "subSignName",
                    "subSignLabel", "subSignError", "subSignSubmit",
                    "compliancePanel", "requestComplianceModal", "rcTitle", "rcRecipients",
                    "rcComment", "rcError", "rcSend",
                    "accessConfirmModal", "accessConfirmTitle", "accessConfirmMsg", "accessConfirmBtn",
                    "feeAuditModal", "feeAuditList",
    "receiptDropZone", "receiptFileInput", "receiptChips", "receiptReadStatus",
    "retainageRow", "retainageLabel", "retainageEdit", "retainageInput",
    "addInvModal", "addInvZone", "addInvInput", "addInvChips", "addInvStatus", "addInvSave", "addInvReview", "addInvPaid", "addInvSeq",
  , "paymentInvoiceList", "paymentInvoiceHint", "apRecordModal", "apRecordSend", "apRecordNote", "mergeModal", "mergeTarget", "mergeBtn"]
  static values = { baseUrl: String, aiStatusUrl: String }

  connect() {
    this.connectPcoAttachments()
    // Fresh PCO page (GC-grid parity, Phelipe 2026-08-18): the cursor
    // starts in the first empty Description so typing can begin at once —
    // and :focus-within lights the row up immediately.
    const firstDesc = this.element.querySelector('[data-pco-lines] input[data-line-field="description"]')
    if (firstDesc && !firstDesc.value) setTimeout(() => firstDesc.focus(), 50)

    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
    // "Add & Sign" (Phelipe 2026-08-18): the entry was just created — open
    // the sign & request modal for it straight away.
    const signId = sessionStorage.getItem("builder-finance.open-sign")
    if (signId && this.hasSubSignModalTarget) {
      sessionStorage.removeItem("builder-finance.open-sign")
      this._subSignUrl = `${this.baseUrlValue}/invoices/${signId}/sign_and_request`
      if (this.hasSubSignLabelTarget) this.subSignLabelTarget.textContent = `pay request #${signId}`
      if (this.hasSubSignErrorTarget) this.subSignErrorTarget.classList.add("hidden")
      this.subSignModalTarget.classList.remove("hidden")
    }
  }

  disconnect() { clearInterval(this._poll) }

  // ── Detail popups (Phelipe 2026-07-23): row click loads details +
  // history from the server; approve/decline lives inside. Clicks on
  // buttons/links inside a row keep their own behavior.
  async openInvoiceDetail(e) {
    if (e.target.closest("button, a, input, select, label")) return
    await this._openDetail(e.currentTarget.dataset.detailUrl, this.invoiceDetailModalTarget)
  }

  async openChangeDetail(e) {
    if (e.target.closest("button, a, input, select, label")) return
    await this._openDetail(e.currentTarget.dataset.detailUrl, this.changeDetailModalTarget)
  }

  async _openDetail(url, modal) {
    if (!url || !modal) return
    try {
      const resp = await fetch(url, { headers: { "Accept": "text/html" } })
      if (!resp.ok) throw new Error()
      modal.querySelector("[data-detail-body]").innerHTML = await resp.text()
      modal.classList.remove("hidden")
    } catch {
      this._showToast("Could not load the details — try again.", "alert")
    }
  }

  // ── Sign & request approval (Phelipe 2026-07-23): GC signs typed, the
  // picked approvers get chained pay-app style. Reload shows the badge.
  openSubSignModal(e) {
    this._subSignUrl = e.currentTarget.dataset.url
    if (this.hasSubSignLabelTarget) this.subSignLabelTarget.textContent = e.currentTarget.dataset.docLabel || "document"
    if (this.hasSubSignErrorTarget) this.subSignErrorTarget.classList.add("hidden")
    // AP instructions only make sense on a pay request; the note to the sub rides every document.
    const isInvoice = (this._subSignUrl || "").includes("/invoices/")
    this.subSignModalTarget.querySelectorAll("[data-sub-sign-invoice-only]").forEach((el) => el.classList.toggle("hidden", !isInvoice))
    this.subSignModalTarget.classList.remove("hidden")
  }

  addSubSignRow() {
    const node = this.subSignRowTemplateTarget.content.firstElementChild
    if (node) this.subSignRowsTarget.appendChild(node.cloneNode(true))
  }

  removeSubSignRow(e) {
    e.currentTarget.closest("[data-sub-sign-row]")?.remove()
  }

  async submitSubSign() {
    const signers = Array.from(this.subSignRowsTarget.querySelectorAll("[data-sub-sign-row]")).map((row) => ({
      name: row.querySelector("[data-sign-name]")?.value?.trim() || "",
      email: row.querySelector("[data-sign-email]")?.value?.trim() || ""
    })).filter((s) => s.name || s.email)

    this.subSignSubmitTarget.disabled = true
    try {
      const resp = await fetch(this._subSignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ signed_name: this.subSignNameTarget.value, signers,
          // Sign-off notes (Larry item 10, 2026-09-01)
          ap_note: this.hasSubSignApNoteTarget ? this.subSignApNoteTarget.value.trim() : "",
          sub_note: this.hasSubSignSubNoteTarget ? this.subSignSubNoteTarget.value.trim() : "" })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not send for approval")
      sessionStorage.setItem("builder-finance.flash", "Signed — approval requests are on their way")
      window.location.reload()
    } catch (err) {
      this.subSignSubmitTarget.disabled = false
      if (this.hasSubSignErrorTarget) {
        this.subSignErrorTarget.textContent = err.message
        this.subSignErrorTarget.classList.remove("hidden")
      }
    }
  }

  // ── Sub-tabs: Fee Schedule / Pay Requests / Change Orders (Phelipe
  // 2026-07-23). Pure show/hide — compliance + scope stay below every tab.
  switchTab(e) {
    const tab = e.currentTarget.dataset.tab
    // Keep the tab in the URL — a refresh (or any post-action reload)
    // reopens the SAME tab instead of bouncing to the default
    // (Phelipe 2026-08-18).
    try {
      const url = new URL(window.location)
      url.searchParams.set("tab", tab)
      history.replaceState(history.state, "", url)
    } catch { /* URL API missing — refresh just lands on the default */ }
    // Company-settings tab look (Phelipe 2026-08-17): brand bold when
    // active, muted otherwise — the bar itself stays white.
    this.element.querySelectorAll("[data-sub-tab-btn]").forEach((b) => {
      const on = b.dataset.tab === tab
      b.classList.toggle("text-brand", on)
      b.classList.toggle("font-bold", on)
      b.classList.toggle("text-text-muted", !on)
      b.classList.toggle("font-medium", !on)
      b.classList.toggle("hover:text-gray-700", !on)
    })
    this.element.querySelectorAll("[data-sub-tab-action]").forEach((b) => {
      const on = b.dataset.subTabAction === tab
      // inline-flex XOR hidden — both at once and the compiled CSS order
      // lets inline-flex win, showing every button.
      b.classList.toggle("hidden", !on)
      b.classList.toggle("inline-flex", on)
    })
    this.element.querySelectorAll("[data-sub-tab-panel]").forEach((p) => {
      p.classList.toggle("hidden", p.dataset.tab !== tab)
    })
  }

  // ── Modals ──
  openInvoiceModal(e) { this._invoiceUrl = e.currentTarget.dataset.url; this.invoiceModalTarget.classList.remove("hidden") }

  // List-row click → the full G702 page (Phelipe 2026-08-18); clicks on
  // links/buttons inside the row keep their own behavior.
  // ── As-needed: stacking invoices (Phelipe 2026-08-31) ────────────────
  // The same purple reader + review/already-paid choice the New
  // Subcontractor popup has, pointed at this contract. Save reloads with
  // the standard flash — totals, lines and the list all move at once.
  openAddInvoice() {
    this._addInvFiles = []
    if (this.hasAddInvSeqTarget) this.addInvSeqTarget.value = ""
    if (this.hasAddInvChipsTarget) this.addInvChipsTarget.innerHTML = ""
    if (this.hasAddInvStatusTarget) this.addInvStatusTarget.classList.add("hidden")
    this.addInvModalTarget.classList.remove("hidden")
  }

  closeAddInvoice() { this.addInvModalTarget.classList.add("hidden") }

  addInvBrowse() { this.addInvInputTarget.click() }
  addInvChosen(e) { Array.from(e.target.files || []).forEach((f) => this._addInvUpload(f)); e.target.value = "" }
  addInvDragOver(e) { e.preventDefault(); this.addInvZoneTarget.classList.add("border-purple-500", "bg-purple-100") }
  addInvDragLeave() { this.addInvZoneTarget.classList.remove("border-purple-500", "bg-purple-100") }
  addInvDrop(e) {
    e.preventDefault()
    this.addInvDragLeave()
    Array.from(e.dataTransfer?.files || []).forEach((f) => this._addInvUpload(f))
  }

  _addInvCard() { return this.element.querySelector("[data-sub-contract-add-inv-save-url]") }

  async _addInvUpload(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const card = this._addInvCard()
    if (!card) return
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-[13px] text-blue-700 max-w-full"
    chip.innerHTML = `<svg class="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="truncate max-w-[220px]">${esc(file.name)}</span>`
    this.addInvChipsTarget.appendChild(chip)
    try {
      const presignResp = await fetch(card.dataset.subContractAddInvPresignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const presign = await presignResp.json()
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "Could not start the upload")
      const { upload_url, cache_name } = presign.uploads[0]
      const put = await fetch(upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error("Upload failed — try again")

      const entry = { cache_name, filename: file.name }
      this._addInvFiles.push(entry)
      chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[13px] text-gray-700 max-w-full"
      chip.innerHTML = `<span class="truncate max-w-[220px]">${esc(file.name)}</span>`
      // First file names the invoice: the reader pulls number / date /
      // amount / line items, and they ride the save.
      if (this._addInvFiles.length === 1) await this._addInvRead(entry, card)
    } catch (err) {
      chip.remove()
      this._showToast(err.message, "alert")
    }
  }

  async _addInvRead(entry, card) {
    const say = (t) => {
      if (!this.hasAddInvStatusTarget) return
      this.addInvStatusTarget.textContent = t
      this.addInvStatusTarget.classList.toggle("hidden", !t)
    }
    say("ConsLog AI — reading the invoice…")
    try {
      const resp = await fetch(card.dataset.subContractAddInvReadUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: entry.cache_name, filename: entry.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "read failed")
      entry.number = data.invoice_number || null
      entry.invoice_date = data.invoice_date || null
      entry.amount = data.amount || null
      entry.description = data.description || null
      entry.line_items = Array.isArray(data.line_items) ? data.line_items : []
      say(data.amount ? `✓ Read it — ${entry.number ? `#${entry.number}, ` : ""}$${Number(data.amount).toLocaleString()}` : "✓ Read the invoice — review before adding")
    } catch {
      say("Couldn't read it — the invoice still attaches; enter amounts on its page after adding.")
    }
  }

  async saveAddInvoice() {
    if (!this._addInvFiles?.length) return this._showToast("Attach the invoice first.", "alert")
    const card = this._addInvCard()
    const status = this.hasAddInvPaidTarget && this.addInvPaidTarget.checked ? "paid" : "received"
    this.addInvSaveTarget.disabled = true
    try {
      const body = { invoice_files: this._addInvFiles, invoice_status: status }
      if (this.hasAddInvSeqTarget && this.addInvSeqTarget.value.trim() !== "") body.invoice_sequence = this.addInvSeqTarget.value.trim()
      const resp = await fetch(card.dataset.subContractAddInvSaveUrl, {
        method: "POST", headers: this._headers(), body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save the invoice — try again.")
      sessionStorage.setItem("builder-finance.flash", status === "paid" ? "Invoice added — recorded as paid" : "Invoice added — waiting on your review")
      window.location.reload()
    } catch (err) {
      this.addInvSaveTarget.disabled = false
      this._showToast(err.message, "alert")
    }
  }

  goToPayRequest(e) {
    if (e.target.closest("a, button, input, label")) return
    window.location = e.currentTarget.dataset.url
  }
  openPaymentModal(e) { this._paymentUrl = e.currentTarget.dataset.url; this.paymentModalTarget.classList.remove("hidden") }

  // Add PCO, GC-flow parity (Phelipe 2026-08-21): create a blank draft and
  // land straight on the full PCO page — same as the GC's own Add PCO.
  async addPco(e) {
    const btn = e.currentTarget
    btn.disabled = true
    try {
      const resp = await fetch(btn.dataset.url, {
        method: "POST", headers: this._headers(), body: JSON.stringify({ status: "draft" })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.id) throw new Error(data.error || "Could not create the PCO")
      window.location = btn.dataset.pageTemplate.replace("CHANGE_ID", data.id)
    } catch (err) {
      btn.disabled = false
      this._toast(err.message, "alert")
    }
  }

  openChangeModal(e) {
    this._changeUrl = e.currentTarget.dataset.url
    // Fresh sheet every time — the line grid, staged files and typed
    // fields all persist in the DOM otherwise.
    this._changeFiles = null
    const modal = this.changeModalTarget
    modal.querySelectorAll("[data-change-field]").forEach((el) => { el.value = ""; el.disabled = false })
    const lines = modal.querySelector("[data-change-lines]")
    if (lines) lines.innerHTML = ""
    const chips = modal.querySelector("[data-change-chips]")
    if (chips) chips.innerHTML = ""
    this.recalcChangeTotal()
    modal.classList.remove("hidden")
  }
  openEditModal() { this.editModalTarget.classList.remove("hidden") }
  openDeleteModal() { this.deleteModalTarget.classList.remove("hidden") }

  async deleteContract(e) {
    const { url, redirect } = e.currentTarget.dataset
    try {
      const resp = await fetch(url, { method: "DELETE", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not delete the sub")
      sessionStorage.setItem("builder-finance.flash", "Sub removed from the project")
      window.location.href = redirect
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  openApproveModal(e) {
    this._approveUrl = e.currentTarget.dataset.url
    this.approveAmountTarget.value = e.currentTarget.dataset.amount
    this.approveWarningTarget.classList.add("hidden")
    this._allowOverbill = false
    this.approveBtnTarget.textContent = "Approve"
    this.approveModalTarget.classList.remove("hidden")
  }

  openComplianceModal(e) {
    const d = e.currentTarget.dataset
    this._complianceUrl = d.url
    const modal = this.complianceModalTarget
    modal.querySelector('[data-compliance-field="doc_type"]').value = d.docType
    modal.querySelector('[data-compliance-field="requirement"]').value = d.requirement || "required"
    modal.classList.remove("hidden")
  }

  closeModals() {
    ;["invoiceModal", "approveModal", "paymentModal", "changeModal", "complianceModal", "editModal", "filesModal", "deleteModal", "pcoDeleteModal", "pcoWithdrawModal", "pcoFinishAmendModal", "memberAccessModal", "invoiceDetailModal", "changeDetailModal", "invDeleteModal", "subSignModal", "requestComplianceModal", "accessConfirmModal", "feeAuditModal", "apRecordModal", "mergeModal"].forEach((m) => {
      if (this[`has${m[0].toUpperCase()}${m.slice(1)}Target`]) this[`${m}Target`].classList.add("hidden")
    })
  }
  stopPropagation(e) { e.stopPropagation() }

  // ── Invoices ──
  // GC-side invoice drop zone (Larry/Phelipe 2026-07-23): drop or browse →
  // presign → S3 PUT (chip with spinner, then gray chip + ✕) → AI reads it
  // and fills the fields automatically. The file registers onto the invoice
  // after create.
  clickInvoiceZone() { this.invoiceFileInputTarget.click() }
  dragOverInvoiceZone(e) {
    e.preventDefault()
    this.invoiceDropZoneTarget.classList.add("border-purple-500", "bg-purple-200")
  }
  dragLeaveInvoiceZone() { this.invoiceDropZoneTarget.classList.remove("border-purple-500", "bg-purple-200") }
  dropOnInvoiceZone(e) {
    e.preventDefault()
    this.dragLeaveInvoiceZone()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._uploadInvoiceFile(file)
  }
  invoiceFileChosen(e) {
    const file = e.target.files[0]
    if (file) this._uploadInvoiceFile(file)
    e.target.value = ""
  }

  // ── Retainage inline edit (Larry 2026-08-18) ──
  // S3-9 (Jason/Starr): per-contract switch — the sub types labor and
  // material apart on each pay application line. Saves on the spot.
  async toggleLaborSplit(e) {
    const box = e.currentTarget
    try {
      const resp = await fetch(box.dataset.url, {
        method: "PATCH", headers: this._headers(),
        body: JSON.stringify({ split_labor_material: box.checked })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not save")
      this._showToast(box.dataset.toast || "Saved")
    } catch (err) {
      box.checked = !box.checked
      this._showToast(err.message, "alert")
    }
  }

  editRetainage() {
    this.retainageEditTarget.classList.remove("hidden")
    this.retainageEditTarget.classList.add("inline-flex")
    this.retainageInputTarget.focus()
  }

  cancelRetainage() {
    this.retainageEditTarget.classList.add("hidden")
    this.retainageEditTarget.classList.remove("inline-flex")
  }

  async saveRetainage(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, {
        method: "PATCH", headers: this._headers(),
        body: JSON.stringify({ retainage_pct: this.retainageInputTarget.value })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not update retainage")
      this.retainageLabelTarget.textContent = `${data.retainage_pct}%`
      this.cancelRetainage()
      this._showToast("Retainage updated")
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // ── Record-payment receipt (Larry 2026-08-18): drop a check copy / ACH
  // receipt → AI fills the payment form; the file stays on the payment. ──
  dragOverReceipt(e) { e.preventDefault(); this.receiptDropZoneTarget.classList.add("border-purple-500", "bg-purple-100") }
  dragLeaveReceipt() { this.receiptDropZoneTarget.classList.remove("border-purple-500", "bg-purple-100") }
  dropReceipt(e) {
    e.preventDefault()
    this.dragLeaveReceipt()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._uploadReceiptFile(file)
  }
  browseReceipt() { this.receiptFileInputTarget.click() }
  receiptChosen(e) {
    const file = e.currentTarget.files?.[0]
    if (file) this._uploadReceiptFile(file)
    e.currentTarget.value = ""
  }

  async _uploadReceiptFile(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const wrap = this.receiptDropZoneTarget.closest("[data-presign-url]")
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span>`
    this.receiptChipsTarget.innerHTML = ""
    this.receiptChipsTarget.appendChild(chip)
    try {
      const presign = await fetch(wrap.dataset.presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")
      const u = data.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      this._receiptCache = { cache_name: u.cache_name, filename: u.filename, read_url: wrap.dataset.readUrl }
      chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg><span class="max-w-[180px] truncate">${esc(u.filename)}</span><button type="button" class="text-gray-400 hover:text-red-500 cursor-pointer" data-action="click->sub-contract#removeReceiptFile">✕</button>`
      this._readReceiptIntoFields()
    } catch (err) {
      this.receiptChipsTarget.innerHTML = ""
      this._receiptCache = null
      this._showToast(err.message, "alert")
    }
  }

  removeReceiptFile() {
    this._receiptCache = null
    this.receiptChipsTarget.innerHTML = ""
    if (this.hasReceiptReadStatusTarget) this.receiptReadStatusTarget.classList.add("hidden")
  }

  async _readReceiptIntoFields() {
    if (!this._receiptCache) return
    const status = this.receiptReadStatusTarget
    status.textContent = "✨ Reading the receipt…"
    status.classList.remove("hidden")
    try {
      const resp = await fetch(this._receiptCache.read_url, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: this._receiptCache.cache_name, filename: this._receiptCache.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the receipt — fill the fields manually")
      const modal = this.paymentModalTarget
      const set = (f, v) => { const el = modal.querySelector(`[data-payment-field="${f}"]`); if (el && v != null && v !== "") el.value = v }
      if (data.amount != null) set("amount", Number(data.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
      set("method", data.method); set("reference", data.reference); set("confirmation_number", data.confirmation_number)
      if (data.paid_on) {
        const el = modal.querySelector('[data-payment-field="paid_on"]')
        if (el) { if (el._flatpickr) el._flatpickr.setDate(data.paid_on, true, "Y-m-d"); else el.value = data.paid_on }
      }
      status.textContent = "✓ Filled from the receipt — review before recording"
    } catch (err) {
      status.textContent = err.message
    }
  }

  // Ticking pay requests fills the amount with the total still owed across
  // them (Larry 2026-08-18 for one; Phelipe 2026-08-28 for several) — still
  // editable for a short-pay, and the server splits an edited total across
  // the ticked requests in order.
  paymentInvoicesChanged() {
    const boxes = this._checkedPaymentInvoices()
    const total = boxes.reduce((sum, b) => sum + Number(b.dataset.outstanding || 0), 0)
    const el = this.paymentModalTarget.querySelector('[data-payment-field="amount"]')
    if (el && boxes.length) {
      el.value = total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    if (this.hasPaymentInvoiceHintTarget) {
      this.paymentInvoiceHintTarget.textContent = boxes.length > 1
        ? `${boxes.length} pay requests selected — one payment is recorded against each, sharing this check's date and reference.`
        : "Tick everything this payment covers — leave all unticked to record a payment not tied to a request."
    }
  }

  _checkedPaymentInvoices() {
    if (!this.hasPaymentModalTarget) return []
    return [...this.paymentModalTarget.querySelectorAll("[data-payment-invoice]")].filter(b => b.checked)
  }

  formatPaymentAmount(e) {
    const n = parseFloat((e.currentTarget.value || "").replace(/[^0-9.\-]/g, ""))
    if (!isNaN(n)) e.currentTarget.value = n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  async _uploadInvoiceFile(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const wrap = this.invoiceDropZoneTarget.closest("[data-presign-url]")
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span>`
    this.invoiceChipsTarget.innerHTML = ""
    this.invoiceChipsTarget.appendChild(chip)
    try {
      const presign = await fetch(wrap.dataset.presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")
      const u = data.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      this._invoiceCache = { cache_name: u.cache_name, filename: u.filename, read_url: wrap.dataset.readUrl }
      chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg><span class="max-w-[180px] truncate">${esc(u.filename)}</span><button type="button" class="text-gray-400 hover:text-red-500 cursor-pointer" data-action="click->sub-contract#removeInvoiceFile">✕</button>`
      this._readInvoiceIntoFields()
    } catch (err) {
      this.invoiceChipsTarget.innerHTML = ""
      this._invoiceCache = null
      this._showToast(err.message, "alert")
    }
  }

  removeInvoiceFile() {
    this._invoiceCache = null
    this.invoiceChipsTarget.innerHTML = ""
    this.invoiceReadStatusTarget.classList.add("hidden")
  }

  async _readInvoiceIntoFields() {
    if (!this._invoiceCache) return
    const status = this.invoiceReadStatusTarget
    status.textContent = "✨ Reading the invoice…"
    status.classList.remove("hidden")
    try {
      const resp = await fetch(this._invoiceCache.read_url, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: this._invoiceCache.cache_name, filename: this._invoiceCache.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the invoice — fill the fields manually")
      const set = (f, v) => { const el = this.invoiceModalTarget.querySelector(`[data-invoice-field="${f}"]`); if (el && v) el.value = v }
      set("number", data.number); set("amount", data.amount); set("period_end", data.period_end)
      if (data.invoice_date || data.period_end) {
        const rec = this.invoiceModalTarget.querySelector('[data-invoice-field="received_on"]')
        const iso = data.invoice_date || data.period_end
        if (rec) { if (rec._flatpickr) rec._flatpickr.setDate(iso, true, "Y-m-d"); else rec.value = iso }
      }
      // Line-matched read (Larry 2026-08-12): AI's amounts land in the
      // VISIBLE per-line grid (Phelipe 2026-08-17) so the builder reviews
      // and can correct each one before adding.
      this._invoiceAmounts = Array.isArray(data.amounts) ? data.amounts : null
      if (this._invoiceAmounts) {
        this._invoiceAmounts.forEach((a) => {
          const input = this.invoiceModalTarget.querySelector(`[data-amount-key="${a.key}"]`)
          if (input) input.value = Number(a.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        })
        this.recalcInvoiceTotal()
      }
      status.textContent = this._invoiceAmounts
        ? `✓ Matched ${this._invoiceAmounts.length} fee schedule ${this._invoiceAmounts.length === 1 ? "line" : "lines"} — review each amount before adding`
        : "✓ Filled from the invoice — please review before adding"
    } catch (err) {
      status.textContent = err.message
    }
  }

  // ── PCO→CO bundling (Larry 2026-08-12) ──
  // The header's red "+ Add Change Order" (Phelipe 2026-08-18): a CO is a
  // bundle of ticked approved PCOs — if none are ticked yet, jump to the
  // PCO tab where the checkboxes live.
  // ── Bundle modal (parity audit CO5, 2026-08-20): the GC's own CO flow —
  // title + description + PCO picker in one pop-up. Ticks already made in
  // the list carry into the modal. ──
  bundleFromHeader() { this.openBundleModal() }

  openBundleModal() {
    if (!this.hasBundleModalTarget) {
      this.element.querySelector('[data-sub-tab-btn][data-tab="proposed_change_orders"]')?.click()
      this._showToast("Approve a proposed change order first — then it can be bundled", "alert")
      return
    }
    // Carry the list's ticks into the picker.
    const listChecked = new Set([...this.element.querySelectorAll("[data-bundle-pco]:checked")].map(b => b.dataset.bundlePco))
    this.bundleModalTarget.querySelectorAll("[data-bundle-modal-pco]").forEach(cb => {
      if (listChecked.size) cb.checked = listChecked.has(cb.dataset.bundleModalPco)
    })
    this.bundleModalTarget.classList.remove("hidden")
  }

  closeBundleModal() {
    if (this.hasBundleModalTarget) this.bundleModalTarget.classList.add("hidden")
  }

  async createBundle(event) {
    const modal = this.bundleModalTarget
    const ids = [...modal.querySelectorAll("[data-bundle-modal-pco]:checked")].map(b => b.dataset.bundleModalPco)
    if (ids.length === 0) {
      this._showToast("Tick at least one proposed change order to bundle", "alert")
      return
    }
    const unlock = this._lock("bundle-pcos", event)
    if (!unlock) return
    try {
      const resp = await fetch(event.currentTarget.dataset.url, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({
          pco_ids: ids,
          title: modal.querySelector("[data-bundle-title]")?.value?.trim() || "",
          description: modal.querySelector("[data-bundle-description]")?.value?.trim() || ""
        })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't bundle those")
      sessionStorage.setItem("builder-finance.flash", `Change order created from ${ids.length} PCO${ids.length === 1 ? "" : "s"}`)
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
      unlock()
    }
  }

  async approveChangeOrder(event) {
    await this._postAndReload(event.currentTarget.dataset.url, "Change order approved")
  }

  async voidChangeOrder(event) {
    await this._postAndReload(event.currentTarget.dataset.url, "Change order voided — its PCOs are loose again")
  }

  // ── Formal change order, GC→owner parity (Phelipe 2026-08-13) ──
  // One popup, one handler: the button carries the verb and the URL, so
  // submit / approve / reject / revise / reopen / void all land here.
  async openChangeOrderDetail(e) {
    if (e.target.closest("button, a, input, select, label")) return
    await this._openDetail(e.currentTarget.dataset.detailUrl, this.changeDetailModalTarget)
  }

  // ── Withdraw an approved CO (parity audit CO8, 2026-08-20): a real modal
  // with an optional reason + billed-work heads-up, not a click-again. ──
  openReopenCo(e) {
    const modal = this.element.querySelector(`[data-reopen-co-modal="${e.currentTarget.dataset.coId}"]`)
    if (modal) modal.classList.remove("hidden")
  }

  closeReopenCo(e) {
    e.currentTarget.closest("[data-reopen-co-modal]")?.classList.add("hidden")
  }

  async confirmReopenCo(e) {
    const btn = e.currentTarget
    const modal = btn.closest("[data-reopen-co-modal]")
    const reason = modal?.querySelector("[data-reopen-co-reason]")?.value?.trim() || ""
    btn.disabled = true
    try {
      const resp = await fetch(btn.dataset.url, { method: "POST", headers: this._headers(),
                                                  body: JSON.stringify({ reason }) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't withdraw the approval")
      sessionStorage.setItem("builder-finance.flash", "Approval withdrawn — back to draft")
      window.location.reload()
    } catch (err) {
      btn.disabled = false
      modal?.classList.add("hidden")
      this._showToast(err.message, "alert")
    }
  }

  async changeOrderAction(e) {
    const btn = e.currentTarget
    const verb = btn.dataset.verb
    const body = {}

    if (verb === "reject") {
      const row = btn.closest("[data-reject-row]")
      const reason = row?.querySelector("[data-reject-reason]")?.value.trim()
      if (!reason) { this._showToast("Say why it's being rejected", "alert"); return }
      body.reason = reason
    }
    // Reopening withdraws signatures — never silently, and never a
    // browser confirm() (app convention: a styled modal or an explicit
    // second click). One inline confirm step keeps it in the popup.
    if (btn.dataset.confirmTitle && btn.dataset.confirmed !== "1") {
      btn.dataset.confirmed = "1"
      btn.textContent = btn.dataset.confirmBody ? "Click again to confirm" : "Click again"
      btn.classList.add("text-red-600", "border-red-200")
      return
    }

    const flashes = {
      submit: "Change order sent to the sub",
      approve: "Change order approved",
      reject: "Change order rejected",
      revise: "Change order reopened for revision",
      reopen: "Approval withdrawn — back to draft",
      withdraw_chain: "Signature request withdrawn — back to draft",
      void: "Change order voided — its PCOs are loose again",
      delete: "Change order deleted — its PCOs are loose again"
    }
    // Delete leaves the page it stood on — go back to the CO tab.
    if (verb === "delete") {
      try {
        const resp = await fetch(btn.dataset.url, { method: "DELETE", headers: this._headers(), body: "{}" })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok) throw new Error(data.error || "Couldn't delete the change order")
        sessionStorage.setItem("builder-finance.flash", flashes.delete)
        window.location = btn.dataset.redirect || window.location.href
      } catch (err) {
        this._showToast(err.message, "alert")
      }
      return
    }
    await this._postAndReload(btn.dataset.url, flashes[verb] || "Done", body)
  }

  async saveChangeOrder(e) {
    const root = e.currentTarget.closest("[data-detail-body]") || this.element
    const body = {
      title: root.querySelector('[data-co-field="title"]')?.value || "",
      description: root.querySelector('[data-co-field="description"]')?.value || ""
    }
    try {
      const resp = await fetch(e.currentTarget.dataset.url, {
        method: "PATCH", headers: this._headers(), body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save that")
      this._showToast("Change order saved")
    } catch (err) { this._showToast(err.message, "alert") }
  }

  async addChangeOrderPco(e) {
    const root = e.currentTarget.closest("[data-detail-body]") || this.element
    const id = root.querySelector("[data-co-add-pco]")?.value
    if (!id) return
    await this._postAndReload(e.currentTarget.dataset.urlTemplate.replace("PCO_ID", id),
                              "Added to the change order")
  }

  async removeChangeOrderPco(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't remove that")
      sessionStorage.setItem("builder-finance.flash", "Removed from the change order")
      window.location.reload()
    } catch (err) { this._showToast(err.message, "alert") }
  }

  // Attach supporting documents to a CO: presign → S3 PUT → register, the
  // same three steps every other upload in the app takes.
  browseChangeOrderFiles(e) {
    const registerUrl = e.currentTarget.dataset.url
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.addEventListener("change", () => {
      const files = [...input.files]
      if (files.length) this._uploadChangeOrderFiles(files, registerUrl)
    })
    input.click()
  }

  async _uploadChangeOrderFiles(files, registerUrl) {
    const presignUrl = this.element.dataset.subContractPresignUrl
    if (!presignUrl) { this._showToast("Uploads aren't available here", "alert"); return }
    this._showToast(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`)
    try {
      const presign = await fetch(presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: files.map(f => ({ filename: f.name, content_type: f.type, byte_size: f.size })) })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")

      // Uploads run in parallel — app-wide convention for drop zones.
      await Promise.all(data.uploads.map((u, i) =>
        fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: files[i] })
          .then(r => { if (!r.ok) throw new Error(`${files[i].name} failed to upload`) })))

      const reg = await fetch(registerUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: data.uploads.map(u => ({ cache_name: u.cache_name, filename: u.filename })) })
      })
      if (!reg.ok) throw new Error("Couldn't attach those files")
      this._showToast(`Attached ${files.length} file${files.length === 1 ? "" : "s"}`)
    } catch (err) { this._showToast(err.message, "alert") }
  }

  async _postAndReload(url, flash, body = null) {
    try {
      const resp = await fetch(url, {
        method: "POST", headers: this._headers(),
        body: body ? JSON.stringify(body) : undefined
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "That didn't work")
      sessionStorage.setItem("builder-finance.flash", flash)
      window.location.reload()
    } catch (err) { this._showToast(err.message, "alert") }
  }

  // The visible per-line grid is the source of truth (Phelipe 2026-08-17):
  // the builder can type amounts with no file, or correct what AI prefilled.
  // %, balance and the header total all follow every keystroke.
  recalcInvoiceTotal() {
    const inputs = Array.from(this.invoiceModalTarget.querySelectorAll("[data-invoice-line]"))
    if (!inputs.length) return
    const money = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    let total = 0
    inputs.forEach((i) => {
      const val = parseFloat(i.value.replace(/[$,\s]/g, "")) || 0
      total += val
      const sched = parseFloat(i.dataset.scheduled) || 0
      const prev = parseFloat(i.dataset.prev) || 0
      const key = i.dataset.amountKey
      const pct = this.invoiceModalTarget.querySelector(`[data-line-pct="${key}"]`)
      if (pct) pct.textContent = sched ? `${(((prev + val) / sched) * 100).toFixed(1)}%` : "—"
      const bal = this.invoiceModalTarget.querySelector(`[data-line-balance="${key}"]`)
      if (bal) bal.textContent = `$${money(sched - prev - val)}`
    })
    const amount = this.invoiceModalTarget.querySelector('[data-invoice-field="amount"]')
    if (amount) amount.value = money(total)
  }

  async createInvoice(e) {
    // "draft" parks it; "sign" creates it and opens the sign & request
    // flow right after reload (Phelipe 2026-08-18) — the review round-trip
    // is for portal submissions.
    const mode = e?.currentTarget?.dataset?.mode || "received"
    const body = this._collect(this.invoiceModalTarget, "data-invoice-field")
    body.mode = mode === "sign" ? "received" : mode
    const lineInputs = Array.from(this.invoiceModalTarget.querySelectorAll("[data-invoice-line]"))
    if (lineInputs.length) {
      body.amounts = lineInputs
        .map((i) => ({ key: i.dataset.amountKey, amount: i.value.trim() }))
        .filter((a) => a.amount && parseFloat(a.amount.replace(/[$,\s]/g, "")) > 0)
    } else if (this._invoiceAmounts) body.amounts = this._invoiceAmounts
    const unlock = this._lock("create-invoice", e)
    if (!unlock) return
    try {
      const resp = await fetch(this._invoiceUrl, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not enter the invoice")
      if (this._invoiceCache && data.id) {
        await fetch(`${this.baseUrlValue}/invoices/${data.id}/register_files`, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ cache_name: this._invoiceCache.cache_name, filename: this._invoiceCache.filename }] })
        })
      }
      if (mode === "sign" && data.id) {
        sessionStorage.setItem("builder-finance.open-sign", String(data.id))
        sessionStorage.setItem("builder-finance.flash", "Saved — add your signature to send it for approval")
      } else {
        sessionStorage.setItem("builder-finance.flash",
          mode === "draft" ? "Draft saved — approve it when you're ready" : "Invoice entered")
      }
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
      unlock()
    }
  }

  async approveInvoice() {
    const body = {
      approved_amount: this.approveAmountTarget.value,
      retainage_pct: this.approveRetainageTarget.value,
      review_notes: this.approveNotesTarget.value,
      ap_note: this.hasApproveApNoteTarget ? this.approveApNoteTarget.value.trim() : "",
      allow_overbill: this._allowOverbill ? "1" : ""
    }
    try {
      const resp = await fetch(this._approveUrl, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) {
        if (data.needs_confirmation) {
          this._allowOverbill = true
          this.approveWarningTarget.textContent = `${data.error} Click "Approve anyway" to override the guard.`
          this.approveWarningTarget.classList.remove("hidden")
          this.approveBtnTarget.textContent = "Approve anyway"
          return
        }
        throw new Error(data.error || "Could not approve")
      }
      sessionStorage.setItem("builder-finance.flash", data.status === "short_paid" ? "Approved with an adjustment — the sub was emailed" : "Invoice approved — the sub was emailed")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  toggleInvoiceLines(e) {
    const row = this.element.querySelector(`[data-invoice-lines="${e.currentTarget.dataset.invoiceLinesId}"]`)
    if (row) row.classList.toggle("hidden")
  }

  async rejectInvoice(e) {
    await this._postReload(e.currentTarget.dataset.url, { review_notes: "" }, "Invoice returned to the sub")
  }

  async viewInvoiceFiles(e) {
    // Callers may pass data-delete-template (URL with ATT_ID) to make files
    // removable, and data-empty-label for their own empty wording — the CO
    // viewer used to say "invoice" on a change order (parity audit CO10).
    const deleteTemplate = e.currentTarget.dataset.deleteTemplate || ""
    const emptyLabel = e.currentTarget.dataset.emptyLabel || "No files yet."
    this.filesModalTarget.classList.remove("hidden")
    this.filesListTarget.innerHTML = '<span class="text-[13px] text-gray-400">Loading…</span>'
    const resp = await fetch(e.currentTarget.dataset.url, { headers: { "Accept": "application/json" } })
    const files = await resp.json()
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.filesListTarget.innerHTML = files.length ? "" : `<span class="text-[13px] text-gray-400">${esc(emptyLabel)}</span>`
    files.forEach((f) => {
      const wrap = document.createElement("span")
      wrap.className = "inline-flex items-center gap-1 bg-gray-100 rounded-lg px-2.5 py-1 text-[13px]"
      wrap.innerHTML = `<a href="${f.url}" target="_blank" class="text-gray-700 hover:text-gray-900 max-w-[200px] truncate">${esc(f.filename)}</a>`
      if (deleteTemplate) {
        const x = document.createElement("button")
        x.type = "button"
        x.textContent = "×"
        x.title = "Remove file"
        x.className = "text-gray-400 hover:text-red-600 cursor-pointer leading-none"
        x.addEventListener("click", async () => {
          try {
            const dresp = await fetch(deleteTemplate.replace("ATT_ID", f.id), { method: "DELETE", headers: this._headers() })
            if (!dresp.ok) throw new Error("Couldn't remove the file")
            wrap.remove()
            if (!this.filesListTarget.querySelector("span.inline-flex")) {
              this.filesListTarget.innerHTML = `<span class="text-[13px] text-gray-400">${esc(emptyLabel)}</span>`
            }
          } catch (err) {
            this._showToast(err.message, "alert")
          }
        })
        wrap.appendChild(x)
      }
      this.filesListTarget.appendChild(wrap)
    })
  }

  // ── Payments ──
  async createPayment() {
    const body = this._collect(this.paymentModalTarget, "data-payment-field")
    body.override_hold = this._overrideHold ? "1" : ""
    // One entry per ticked pay request; the server records a payment against
    // each. Empty array = an untied payment, same as before.
    body.invoice_ids = this._checkedPaymentInvoices().map(b => b.dataset.paymentInvoice)
    if (this._receiptCache) {
      body.receipt_cache = this._receiptCache.cache_name
      body.receipt_filename = this._receiptCache.filename
    }
    try {
      const resp = await fetch(this._paymentUrl, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) {
        if (data.compliance_hold) {
          this._overrideHold = true
          this.paymentWarningTarget.textContent = `${data.error}`
          this.paymentWarningTarget.classList.remove("hidden")
          this.paymentBtnTarget.textContent = "Pay anyway (override hold)"
          return
        }
        throw new Error(data.error || "Could not record the payment")
      }
      sessionStorage.setItem("builder-finance.flash", "Payment recorded — the sub was emailed a receipt")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // "Email AP to Record Payment" (Larry item 9, 2026-09-01): pick the
  // approved pay requests with no payment recorded yet; Accounts Payable
  // gets one email per request with its record-payment link.
  openApRecordModal() {
    if (this.hasApRecordModalTarget) this.apRecordModalTarget.classList.remove("hidden")
  }

  async sendApRecordRequest() {
    const modal = this.apRecordModalTarget
    const url = modal.querySelector("[data-url]")?.dataset.url
    const ids = [...modal.querySelectorAll("[data-ap-record-invoice]")].filter(b => b.checked).map(b => b.dataset.apRecordInvoice)
    if (!url) return
    if (ids.length === 0) return this._showToast("Tick at least one pay request.", "alert")
    if (this.hasApRecordSendTarget) this.apRecordSendTarget.disabled = true
    try {
      const note = this.hasApRecordNoteTarget ? this.apRecordNoteTarget.value.trim() : ""
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify({ invoice_ids: ids, note }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not send the email")
      if (this.hasApRecordNoteTarget) this.apRecordNoteTarget.value = ""
      this.closeModals()
      this._showToast(`Email sent to accounts payable (${data.to.join(", ")}) for ${data.sent} pay request${data.sent === 1 ? "" : "s"}`)
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      if (this.hasApRecordSendTarget) this.apRecordSendTarget.disabled = false
    }
  }

  // A moved-as-paid payment has no date; the row carries a date box (Larry item 14, 2026-09-01).
  async savePaidDate(e) {
    const btn = e.currentTarget
    const cell = btn.closest("[data-paid-date-cell]")
    const input = cell && cell.querySelector("[data-paid-date-input]")
    if (!input || !input.value) return this._showToast("Pick the date it was paid.", "alert")
    btn.disabled = true
    try {
      const resp = await fetch(btn.dataset.url, { method: "PATCH", headers: this._headers(), body: JSON.stringify({ paid_on: input.value }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not save the date")
      cell.replaceWith(document.createTextNode(data.paid_on || input.value))
      this._showToast("Paid date saved")
    } catch (err) {
      this._showToast(err.message, "alert")
      btn.disabled = false
    }
  }

  // Merge this sub into another one (Larry 2026-09-01).
  openMergeModal() {
    if (this.hasMergeModalTarget) this.mergeModalTarget.classList.remove("hidden")
  }

  async doMerge(e) {
    const url = e.currentTarget.dataset.url
    const targetId = this.hasMergeTargetTarget ? this.mergeTargetTarget.value : ""
    if (!targetId) return this._showToast("Pick the sub to merge into.", "alert")
    if (this.hasMergeBtnTarget) this.mergeBtnTarget.disabled = true
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify({ target_id: targetId }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not merge")
      sessionStorage.setItem("builder-finance.flash", data.message)
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
      if (this.hasMergeBtnTarget) this.mergeBtnTarget.disabled = false
    }
  }

  async deletePayment(e) {
    await this._postReload(e.currentTarget.dataset.url, {}, "Payment removed", "DELETE")
  }

  async markWaiverReceived(e) {
    await this._postReload(e.currentTarget.dataset.url, { status: "received" }, "Waiver marked received", "PATCH")
  }

  // ── Changes / compliance / edit / portal ──
  // ── Proposed change order form (Phelipe 2026-08-13) — priced lines +
  // markup, saved as a draft or sent straight for approval.
  // GC-form parity (Phelipe 2026-08-18): the same purple AI quote drop the
  // builder's New PCO modal has — reads the quote, fills the form + lines.
  async changeQuoteChosen(e) {
    const input = e.currentTarget
    const file = input.files && input.files[0]
    input.value = ""
    if (!file) return
    await this._readChangeQuote(file, input.closest("[data-change-quote-wrap]"))
  }

  // Drag-and-drop onto the purple zone (Phelipe 2026-08-18) — inline
  // highlight only, per the drop-zone house rule.
  changeQuoteDragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("border-purple-500", "bg-purple-100")
  }

  changeQuoteDragLeave(e) {
    e.currentTarget.classList.remove("border-purple-500", "bg-purple-100")
  }

  async changeQuoteDrop(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("border-purple-500", "bg-purple-100")
    const file = e.dataTransfer?.files?.[0]
    if (file) await this._readChangeQuote(file, e.currentTarget.closest("[data-change-quote-wrap]"))
  }

  async _readChangeQuote(file, wrap) {
    const status = wrap.querySelector("[data-change-quote-status]")
    status.textContent = "✨ Reading the quote…"
    status.classList.remove("hidden")
    try {
      const presign = await fetch(wrap.dataset.presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Could not start the upload")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      // The quote itself becomes a supporting document on the new PCO —
      // shown on its page and appended to the PDF export.
      this._changeFiles = (this._changeFiles || []).concat([{ cache_name: u.cache_name, filename: file.name }])
      this._renderChangeChips()
      const resp = await fetch(wrap.dataset.readUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: u.cache_name, filename: file.name })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the quote — fill the form manually")

      const set = (f, v) => { const el = this.changeModalTarget.querySelector(`[data-change-field="${f}"]`); if (el && v != null && v !== "") el.value = v }
      set("title", data.title)
      set("description", data.description)
      if (data.days) set("time_extension_days", data.days)
      const lines = Array.isArray(data.lines) ? data.lines : []
      // The lines ride to the PCO page after Create (Phelipe 2026-08-18) —
      // the popup stays small, like the GC one.
      this._quoteChangeLines = lines.length ? lines : null
      status.textContent = lines.length
        ? `✓ Read the quote — ${lines.length} ${lines.length === 1 ? "line" : "lines"} will appear on the PCO page, file attached`
        : "✓ Read the quote — review before creating (file attached)"
    } catch (err) {
      status.textContent = err.message
    }
  }

  addChangeLine() {
    const body = this.changeModalTarget.querySelector("[data-change-lines]")
    const row = document.createElement("tr")
    row.dataset.changeLine = ""
    const cell = (field, extra = "") =>
      `<td class="px-1 py-1"><input type="text" data-line-field="${field}" data-action="input->sub-contract#recalcChangeTotal" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px] ${extra}"></td>`
    row.innerHTML =
      cell("item") + cell("description") + cell("quantity", "text-right") + cell("unit") +
      cell("unit_price", "text-right") +
      `<td class="px-2 py-1 text-right tabular-nums whitespace-nowrap" data-line-total>$0.00</td>` +
      `<td class="px-1 py-1 text-center"><button type="button" data-action="click->sub-contract#removeChangeLine" class="text-gray-300 hover:text-red-500 cursor-pointer">✕</button></td>`
    body.appendChild(row)
    row.querySelector('[data-line-field="item"]')?.focus()
    this.recalcChangeTotal()
  }

  removeChangeLine(e) {
    e.currentTarget.closest("[data-change-line]")?.remove()
    this.recalcChangeTotal()
  }

  // Lines drive the total whenever there are any; markup rides on top.
  // With no lines the typed amount stands, so the older plain-amount way
  // of raising a change still works exactly as before.
  recalcChangeTotal() {
    const modal = this.changeModalTarget
    const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) ? n : 0 }
    const rows = [...modal.querySelectorAll("[data-change-line]")]
    let linesTotal = 0

    rows.forEach((row) => {
      const qty = num(row.querySelector('[data-line-field="quantity"]')?.value)
      const price = num(row.querySelector('[data-line-field="unit_price"]')?.value)
      const amount = Math.round(qty * price * 100) / 100
      linesTotal += amount
      const cell = row.querySelector("[data-line-total]")
      if (cell) cell.textContent = this._usd(amount)
    })

    const empty = modal.querySelector("[data-change-lines-empty]")
    if (empty) empty.classList.toggle("hidden", rows.length > 0)
    const amountField = modal.querySelector('[data-change-field="amount"]')
    if (amountField) amountField.disabled = rows.length > 0

    const markup = num(modal.querySelector('[data-change-field="markup_pct"]')?.value)
    const base = rows.length ? linesTotal : num(amountField?.value)
    const total = Math.round((base + base * markup / 100) * 100) / 100
    const out = modal.querySelector("[data-change-total]")
    if (out) out.textContent = this._usd(total)
    return total
  }

  // Repaints the staged-files chips from this._changeFiles (used by the
  // quote drop zone; _stageChangeFiles paints its own inline).
  _renderChangeChips() {
    const chips = this.changeModalTarget.querySelector("[data-change-chips]")
    if (!chips) return
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    chips.innerHTML = ""
    ;(this._changeFiles || []).forEach((f) => {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg><span class="max-w-[180px] truncate">${esc(f.filename)}</span>`
      chips.appendChild(chip)
    })
  }

  browseChangeFiles() {
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.addEventListener("change", () => {
      const files = [...input.files]
      if (files.length) this._stageChangeFiles(files)
    })
    input.click()
  }

  // Files are uploaded to S3 now and REGISTERED after the PCO exists —
  // same two-step the invoice modal uses, since there's no id to attach
  // to until create returns.
  async _stageChangeFiles(files) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const chips = this.changeModalTarget.querySelector("[data-change-chips]")
    const presignUrl = this.element.dataset.subContractPresignUrl
    if (!presignUrl) { this._showToast("Uploads aren't available here", "alert"); return }

    const pending = files.map((f) => {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-lg px-2.5 py-1 text-[13px]"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${esc(f.name)}</span>`
      chips.appendChild(chip)
      return chip
    })

    try {
      const presign = await fetch(presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: files.map(f => ({ filename: f.name, content_type: f.type, byte_size: f.size })) })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")

      await Promise.all(data.uploads.map((u, i) =>
        fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: files[i] })
          .then(r => { if (!r.ok) throw new Error(`${files[i].name} failed to upload`) })))

      this._changeFiles = (this._changeFiles || []).concat(
        data.uploads.map(u => ({ cache_name: u.cache_name, filename: u.filename })))

      pending.forEach((chip, i) => {
        chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
        chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg><span class="max-w-[180px] truncate">${esc(data.uploads[i].filename)}</span>`
      })
    } catch (err) {
      pending.forEach(c => c.remove())
      this._showToast(err.message, "alert")
    }
  }

  // Double-click shield (Phelipe 2026-08-18: fast double taps on Create
  // PCO minted duplicates). First call takes the lock; the button greys
  // out until _unlock — callers unlock on failure only, success repaints
  // or navigates.
  _lock(key, event) {
    this._locks ||= {}
    if (this._locks[key]) return null
    this._locks[key] = true
    const btn = event?.currentTarget
    if (btn) { btn.disabled = true; btn.classList.add("opacity-60", "pointer-events-none") }
    return () => {
      this._locks[key] = false
      if (btn) { btn.disabled = false; btn.classList.remove("opacity-60", "pointer-events-none") }
    }
  }

  async createChange(e) {
    // GC-popup flow (Phelipe 2026-08-18): the small popup creates a DRAFT,
    // then the full PCO page opens — lines (from the AI quote, if one was
    // dropped) and documents live there.
    const unlock = this._lock("create-change", e)
    if (!unlock) return
    const body = this._collect(this.changeModalTarget, "data-change-field")
    body.status = "draft"
    if (this._quoteChangeLines?.length) body.lines = this._quoteChangeLines
    if (this._changeFiles?.length) body.files = this._changeFiles
    try {
      const resp = await fetch(this._changeUrl, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not create the PCO")
      sessionStorage.setItem("builder-finance.flash", "PCO created")
      window.location = `${this.baseUrlValue}/pco/${data.id}`
    } catch (err) {
      this._showToast(err.message, "alert")
      unlock()
    }
  }

  // ── PCO page autosave (Phelipe 2026-08-18, GC-page parity) ──
  pcoFieldChanged() { this._pcoQueueSave() }

  pcoAddLine() {
    const body = this.element.querySelector("[data-pco-lines]")
    const row = document.createElement("tr")
    row.dataset.pcoLine = ""
    const cell = (field, extra = "", value = "") =>
      `<td class="px-1 py-1"><input type="text" data-line-field="${field}" value="${value}" data-action="input->sub-contract#pcoFieldChanged" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px] ${extra}"></td>`
    // GC-grid behaviors (Phelipe 2026-08-18): new rows auto-number, the
    // unit price carries the fixed $ like the builder PCO page.
    const nextItem = body.querySelectorAll("[data-pco-line]").length + 1
    row.innerHTML =
      cell("item", "", String(nextItem)) + cell("description") + cell("quantity", "text-right") + cell("unit") +
      `<td class="px-1 py-1"><div class="flex items-center gap-1"><span class="text-gray-400 text-[13px]">$</span><input type="text" data-line-field="unit_price" data-action="input->sub-contract#pcoFieldChanged" class="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[13px] text-right"></div></td>` +
      `<td class="px-2 py-1 text-right tabular-nums whitespace-nowrap" data-pco-line-total>$0.00</td>` +
      `<td class="px-1 py-1 text-center"><button type="button" data-action="click->sub-contract#pcoRemoveLine" class="text-gray-300 hover:text-red-500 cursor-pointer" tabindex="-1">✕</button></td>`
    body.appendChild(row)
    // Same as the GC grid: typing starts in Description, not Item.
    row.querySelector('[data-line-field="description"]')?.focus()
  }

  pcoRemoveLine(e) {
    e.currentTarget.closest("[data-pco-line]")?.remove()
    this._pcoQueueSave()
  }

  _pcoQueueSave() {
    this._recalcPcoTotals()
    clearTimeout(this._pcoTimer)
    this._pcoTimer = setTimeout(() => this._pcoSave(), 700)
  }

  _recalcPcoTotals() {
    const money = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    let total = 0
    this.element.querySelectorAll("[data-pco-line]").forEach((row) => {
      const get = (f) => parseFloat((row.querySelector(`[data-line-field="${f}"]`)?.value || "").replace(/[$,\s]/g, "")) || 0
      const line = get("quantity") * get("unit_price")
      total += line
      const cell = row.querySelector("[data-pco-line-total]")
      if (cell) cell.textContent = `$${money(line)}`
    })
    const sum = this.element.querySelector("[data-pco-lines-total]")
    if (sum) sum.textContent = `$${money(total)}`
    const proposed = this.element.querySelector("[data-pco-proposed]")
    if (proposed) proposed.textContent = `$${money(total)}`
  }

  async _pcoSave() {
    const wrap = this.element.querySelector("[data-pco-editor]")
    if (!wrap) return
    const status = this.element.querySelector("[data-pco-save-status]")
    const body = {
      lines: [...this.element.querySelectorAll("[data-pco-line]")].map((row) => {
        const get = (f) => row.querySelector(`[data-line-field="${f}"]`)?.value || ""
        return { item: get("item"), description: get("description"), quantity: get("quantity"),
                 unit: get("unit"), unit_price: get("unit_price") }
      })
    }
    this.element.querySelectorAll("[data-pco-field]").forEach((f) => { body[f.dataset.pcoField] = f.value })
    try {
      const resp = await fetch(wrap.dataset.updateUrl, { method: "PATCH", headers: this._headers(), body: JSON.stringify(body) })
      if (!resp.ok) throw new Error("save failed")
      if (status) { status.textContent = "Saved"; setTimeout(() => { status.textContent = "" }, 1500) }
    } catch (_e) {
      if (status) status.textContent = "Couldn't save — check your connection"
    }
  }

  // ── PCO supporting documents (Phelipe 2026-08-18, GC-page parity) ──
  connectPcoAttachments() {
    const card = this.element.querySelector("[data-pco-attachments-url]")
    if (card) this._loadPcoAttachments()
  }

  async _loadPcoAttachments() {
    const card = this.element.querySelector("[data-pco-attachments-url]")
    const list = card?.querySelector("[data-pco-attachment-list]")
    if (!list) return
    try {
      const resp = await fetch(card.dataset.pcoAttachmentsUrl, { headers: { Accept: "application/json" } })
      const files = await resp.json()
      list.innerHTML = files.length ? "" : '<span class="text-[13px] text-gray-400">No files attached.</span>'
      files.forEach((f) => list.insertAdjacentHTML("beforeend", this._pcoChipHtml(f)))
    } catch {
      list.innerHTML = '<span class="text-[13px] text-gray-400">Could not load files.</span>'
    }
  }

  _pcoChipHtml(f) {
    const esc = (x) => (x || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    return `<span class="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]" data-attachment-id="${f.id}">
      <a href="${f.url}" target="_blank" class="hover:text-brand max-w-[180px] truncate">${esc(f.filename)}</a>
      <button data-action="click->sub-contract#pcoRemoveAttachment" data-id="${f.id}" class="text-gray-400 hover:text-red-500 cursor-pointer" tabindex="-1">✕</button>
    </span>`
  }

  pcoDragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("ring-2", "ring-brand", "bg-brand/5")
  }

  pcoDragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
  }

  async pcoDropFiles(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
    await this._uploadPcoFiles(Array.from(e.dataTransfer?.files || []))
  }

  async pcoFilesChosen(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    await this._uploadPcoFiles(files)
  }

  async _uploadPcoFiles(files) {
    const card = this.element.querySelector("[data-pco-attachments-url]")
    const presignUrl = this.element.dataset.subContractPresignUrl
    if (!card || !presignUrl) return
    let firstUpload = null
    for (const file of files) {
      try {
        const presign = await fetch(presignUrl, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
        })
        const pd = await presign.json()
        if (!presign.ok) throw new Error(pd.error || "Upload refused")
        const u = pd.uploads[0]
        await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
        await fetch(card.dataset.pcoRegisterUrl, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ cache_name: u.cache_name, filename: u.filename }] })
        })
        firstUpload ||= { cache_name: u.cache_name, filename: file.name }
      } catch (err) {
        this._showToast(`${file.name}: ${err.message}`, "alert")
      }
    }
    this._loadPcoAttachments()
    // Quote-fill (Phelipe 2026-08-21): the first dropped file also reads
    // into the PCO — same AI the old create-modal ran. The file stays
    // attached either way; only untouched fields get filled.
    if (firstUpload && card.dataset.pcoReadQuoteUrl && this.element.querySelector("[data-pco-editor]")) {
      await this._readQuoteIntoPco(card, firstUpload)
    }
  }

  async _readQuoteIntoPco(card, upload) {
    const status = this.element.querySelector("[data-pco-quote-status]")
    const say = (text) => { if (status) { status.textContent = text; status.classList.remove("hidden") } }
    say("✨ Reading the quote…")
    try {
      const resp = await fetch(card.dataset.pcoReadQuoteUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: upload.cache_name, filename: upload.filename })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't read the quote — fill the form manually.")

      const field = (name) => this.element.querySelector(`[data-pco-field="${name}"]`)
      const fillIf = (name, value) => {
        const el = field(name)
        if (el && value && !(el.value || "").trim().replace(/^0$/, "")) el.value = value
      }
      fillIf("title", data.title)
      fillIf("description", data.description)
      fillIf("time_extension_days", data.days && Number(data.days) > 0 ? String(data.days) : null)

      // Lines land only on an untouched grid — never over typed work.
      const rows = [...this.element.querySelectorAll("[data-pco-line]")]
      const gridEmpty = rows.every((row) => {
        const get = (f) => (row.querySelector(`[data-line-field="${f}"]`)?.value || "").trim()
        return !get("description") && !get("unit_price")
      })
      let lines = Array.isArray(data.lines) ? data.lines : []
      if (!lines.length && data.amount) {
        lines = [{ item: "1", description: "Per attached quote", quantity: 1, unit: "LS", unit_price: data.amount }]
      }
      if (gridEmpty && lines.length) {
        const body = this.element.querySelector("[data-pco-lines]")
        rows.forEach((row) => row.remove())
        lines.forEach((l, i) => {
          this.pcoAddLine()
          const row = body.querySelectorAll("[data-pco-line]")[i]
          const set = (f, v) => { const el = row.querySelector(`[data-line-field="${f}"]`); if (el && v != null && v !== "") el.value = String(v) }
          set("item", l.item || String(i + 1)); set("description", l.description)
          set("quantity", l.quantity); set("unit", l.unit); set("unit_price", l.unit_price)
        })
      }
      this._pcoQueueSave()
      say(lines.length && gridEmpty
        ? `✓ Filled ${lines.length} ${lines.length === 1 ? "line" : "lines"} from the quote — review before sending`
        : "✓ Read the quote — the file is attached; review the form")
    } catch (err) {
      say(err.message)
    }
  }

  async pcoRemoveAttachment(e) {
    const card = this.element.querySelector("[data-pco-attachments-url]")
    const chip = e.currentTarget.closest("[data-attachment-id]")
    const id = e.currentTarget.dataset.id
    const resp = await fetch(`${card.dataset.pcoAttachmentsUrl}/${id}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) {
      chip?.remove()
      const list = card.querySelector("[data-pco-attachment-list]")
      if (list && !list.querySelector("[data-attachment-id]")) {
        list.innerHTML = '<span class="text-[13px] text-gray-400">No files attached.</span>'
      }
    } else {
      this._showToast("Couldn't remove the file", "alert")
    }
  }

  // Deleting a PCO asks first (styled modal — house rule), then lands on
  // the contract's Proposed Change Orders tab, not the default tab.
  deleteChange(e) {
    this._pcoDeleteUrl = e.currentTarget.dataset.url
    this.pcoDeleteModalTarget.classList.remove("hidden")
  }

  async amendChange(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, { method: "POST", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't open the amend")
      sessionStorage.setItem("builder-finance.flash", "Amending — edit away, acknowledgments kept")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  openFinishAmend() {
    this.pcoFinishAmendModalTarget.classList.remove("hidden")
    this.pcoAmendReasonTarget?.focus()
  }

  async confirmFinishAmend() {
    try {
      const resp = await fetch(this.pcoFinishAmendModalTarget.dataset.url, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ reason: this.pcoAmendReasonTarget?.value || "" })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't finish the amend")
      sessionStorage.setItem("builder-finance.flash", "Amend finished — acknowledged again")
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  openPcoWithdraw() {
    this.pcoWithdrawModalTarget.classList.remove("hidden")
  }

  async confirmPcoWithdraw() {
    try {
      const resp = await fetch(this.pcoWithdrawModalTarget.dataset.url, { method: "POST", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't withdraw the PCO")
      sessionStorage.setItem("builder-finance.flash", "PCO withdrawn — back to draft")
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  // Remove an invoice entered in error (Phelipe 2026-09-01). The row's
  // click opens the pay request, so this stops propagation first.
  askDeleteInvoice(e) {
    e.preventDefault()
    e.stopPropagation()
    const btn = e.currentTarget
    this._invDeleteUrl = btn.dataset.url

    const paid = parseFloat((btn.dataset.paid || "0").replace(/[^0-9.]/g, "")) || 0
    let note = `"${btn.dataset.label}" and anything billed on it will be removed. This can't be undone.`
    if (paid > 0) note += ` Its recorded payment of $${btn.dataset.paid} is removed too.`
    if (this.hasInvDeleteNoteTarget) this.invDeleteNoteTarget.textContent = note
    if (this.hasInvDeleteModalTarget) this.invDeleteModalTarget.classList.remove("hidden")
  }

  async confirmDeleteInvoice() {
    try {
      const resp = await fetch(this._invDeleteUrl, { method: "DELETE", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't delete the invoice")
      sessionStorage.setItem("builder-finance.flash", "Invoice deleted")
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  async confirmDeleteChange() {
    try {
      const resp = await fetch(this._pcoDeleteUrl, { method: "DELETE", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't delete the PCO")
      sessionStorage.setItem("builder-finance.flash", "PCO deleted")
      window.location = `${this.baseUrlValue}?tab=proposed_change_orders`
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  // Draft → send, rejected → revise: the button carries where to go and
  // what to say afterwards. Shared by the PCO and pay-request popups.
  async changeAction(e) {
    const btn = e.currentTarget
    await this._postAndReload(btn.dataset.url, btn.dataset.flash || "Done")
  }

  // A nudge doesn't change anything on the page, so no reload — just say
  // it went.
  async followupInvoice(e) {
    const btn = e.currentTarget
    btn.disabled = true
    try {
      const resp = await fetch(btn.dataset.url, { method: "POST", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't send that")
      this._showToast("Follow-up sent")
    } catch (err) {
      btn.disabled = false
      this._showToast(err.message, "alert")
    }
  }

  // Portal-submitted change orders (locked design 2026-07-21): one-click
  // approve; reject reveals an inline reason input first (no browser prompt).
  toggleRejectReason(e) {
    const wrap = e.currentTarget.closest("[data-change-id], [data-change-order-id]")
    const row = wrap?.querySelector("[data-reject-row]")
    if (row) row.classList.toggle("hidden")
  }

  _usd(n) {
    return `$${(Math.round(n * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  async decideChange(e) {
    const decision = e.currentTarget.dataset.decision
    const wrap = e.currentTarget.closest("[data-change-id]")
    const reason = wrap?.querySelector("[data-reject-reason]")?.value || ""
    const body = { status: decision }
    if (decision === "rejected") body.reject_reason = reason
    const flashMsg = decision === "approved"
      ? "Change order approved — the sub's contract value is updated"
      : "Change order rejected — the sub was notified"
    await this._postReload(e.currentTarget.dataset.url, body, flashMsg, "PATCH")
  }

  async saveCompliance() {
    const body = this._collect(this.complianceModalTarget, "data-compliance-field")
    await this._postReload(this._complianceUrl, body, "Compliance updated")
  }

  // Request popup (Phelipe 2026-08-17) — mirrors Subcontractor Management:
  // pick who at the sub's company gets the email, add an optional note.
  async openRequestComplianceModal(e) {
    const d = e.currentTarget.dataset
    this._rcUrl = d.url
    this.rcTitleTarget.textContent = `Request ${d.docLabel || "document"}`
    this.rcCommentTarget.value = ""
    this.rcErrorTarget.classList.add("hidden")
    this.rcSendTarget.disabled = false
    this.rcRecipientsTarget.innerHTML = '<p class="text-[13px] text-gray-400">Loading people…</p>'
    this.requestComplianceModalTarget.classList.remove("hidden")

    const panel = this.compliancePanelTarget.dataset
    const rows = []
    const seen = new Set()
    const push = (name, email, primary) => {
      if (!email || seen.has(email.toLowerCase())) return
      seen.add(email.toLowerCase())
      rows.push({ name: name || email, email, primary })
    }
    push(panel.contactName, panel.contactEmail, true)
    if (panel.vendorId) {
      try {
        const resp = await fetch(`${panel.vendorUsersUrl}?id=${encodeURIComponent(panel.vendorId)}`,
          { headers: { Accept: "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (data.contact) push(data.contact.name, data.contact.email, true)
          ;(data.users || []).forEach((u) => push(u.name, u.email, false))
        }
      } catch (_e) { /* the contact row above still renders */ }
    }

    if (!rows.length) {
      this.rcRecipientsTarget.innerHTML =
        '<p class="text-[13px] text-gray-500">No people on file — add the sub\'s contact email first.</p>'
      this.rcSendTarget.disabled = true
      return
    }
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.rcRecipientsTarget.innerHTML = rows.map((r, i) => `
      <label class="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
        <input type="checkbox" data-rc-recipient value="${esc(r.email)}" ${i === 0 ? "checked" : ""}
               class="shrink-0 rounded border-gray-300 text-brand focus:ring-brand">
        <span class="min-w-0">
          <span class="block text-[13px] text-gray-900 truncate">${esc(r.name)}${r.primary ? ' <span class="text-gray-400">· primary contact</span>' : ""}</span>
          <span class="block text-[13px] text-gray-500 truncate">${esc(r.email)}</span>
        </span>
      </label>`).join("")
  }

  async submitRequestCompliance() {
    const emails = Array.from(this.rcRecipientsTarget.querySelectorAll("[data-rc-recipient]:checked"))
      .map((box) => box.value)
    if (!emails.length) {
      this.rcErrorTarget.textContent = "Pick at least one person."
      this.rcErrorTarget.classList.remove("hidden")
      return
    }
    this.rcErrorTarget.classList.add("hidden")
    this.rcSendTarget.disabled = true
    try {
      const resp = await fetch(this._rcUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ emails, comment: this.rcCommentTarget.value.trim() })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not send the request")
      this.closeModals()
      this._showToast(`Request sent to ${emails.length} ${emails.length === 1 ? "person" : "people"}`)
    } catch (err) {
      this.rcErrorTarget.textContent = err.message
      this.rcErrorTarget.classList.remove("hidden")
    } finally {
      this.rcSendTarget.disabled = false
    }
  }

  // Builder files the document right here (Phelipe 2026-08-17) — presign →
  // S3 PUT → the vendor-management register endpoint, so the SAME AI check
  // runs and the vendor record updates; the panel re-seeds from it on reload.
  async uploadCompliance(e) {
    const input = e.currentTarget
    const file = input.files && input.files[0]
    if (!file) return

    const panel = this.compliancePanelTarget.dataset
    const label = input.closest("label")
    const text = label?.querySelector("span")
    const original = text?.textContent
    const docType = input.dataset.docType
    input.value = ""
    if (text) text.textContent = "Checking…"

    try {
      const presignResp = await fetch(panel.presignUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ id: panel.vendorId, filename: file.name,
                               content_type: file.type || "application/octet-stream", byte_size: file.size })
      })
      const presign = await presignResp.json().catch(() => ({}))
      if (!presignResp.ok) throw new Error(presign.error || "That file couldn't be prepared for upload.")

      const put = await fetch(presign.upload_url, {
        method: "PUT", headers: { "Content-Type": presign.content_type }, body: file
      })
      if (!put.ok) throw new Error("The upload didn't finish — try again.")

      const registerResp = await fetch(panel.uploadUrl, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ id: panel.vendorId, doc_type: docType,
                               cache_name: presign.cache_name, filename: file.name })
      })
      const data = await registerResp.json().catch(() => ({}))
      if (!registerResp.ok) throw new Error(data.error || "That document wasn't accepted.")
      if (!data.ok) throw new Error((data.issues || []).join(" ") || "That document wasn't accepted.")

      sessionStorage.setItem("builder-finance.flash", "Document accepted — compliance updated")
      window.location.reload()
    } catch (err) {
      if (text) text.textContent = original
      this._showToast(err.message, "alert")
    }
  }

  addEditCustomDoc(e) {
    const wrap = e.currentTarget.closest("[data-custom-doc-wrap]")
    const input = wrap.querySelector("[data-custom-doc-input]")
    const name = (input.value || "").trim()
    if (!name) return
    input.value = ""
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    wrap.insertAdjacentHTML("beforebegin",
      `<label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer"><input type="checkbox" checked data-edit-custom-doc="${esc(name)}" class="rounded border-gray-300 text-brand focus:ring-brand">${esc(name)}</label>`)
  }

  async saveEdit() {
    const body = this._collect(this.editModalTarget, "data-edit-field")
    // Required-docs checklist is editable after creation too (Phelipe
    // 2026-07-25) — unchecked optional/custom slots get removed server-side.
    body.required_docs = [...this.editModalTarget.querySelectorAll("[data-edit-required-doc]:checked")].map((el) => el.dataset.editRequiredDoc)
    body.custom_docs = [...this.editModalTarget.querySelectorAll("[data-edit-custom-doc]:checked")].map((el) => el.dataset.editCustomDoc)
    await this._postReload(this.baseUrlValue, body, "Contract updated", "PATCH")
  }

  // GC-side qty fix on a fee schedule line (Phelipe 2026-07-28) — value and
  // total update in place.
  async updateFeeQty(e) {
    const input = e.currentTarget
    try {
      const resp = await fetch(input.dataset.url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify({ quantity: input.value })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not update the quantity")
      input.value = data.qty
      const row = input.closest("tr")
      const valueCell = row?.querySelector("[data-fee-line-value]")
      if (valueCell) valueCell.textContent = `$${data.value}`
      const total = this.element.querySelector("[data-fee-schedule-total]")
      if (total) total.textContent = `$${data.total}`
      this._showToast("Quantity updated")
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // Re-email the portal link to the sub (Phelipe 2026-07-27) — stays on the
  // page, just a toast.
  async resendPortalLink(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not resend the portal link")
      this._showToast(`Portal link emailed to ${data.email}`)
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  async togglePortal(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not toggle the portal")
      sessionStorage.setItem("builder-finance.flash", data.enabled ? "Portal on — invite emailed to the sub" : "Portal disabled — old links are dead")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // ── A4 invoice audit ──
  async aiAudit(e) {
    const btn = e.currentTarget
    btn.disabled = true
    btn.innerHTML = "Auditing…"
    try {
      const resp = await fetch(btn.dataset.url, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not start the audit")
      this._pollAudit(data.run_id)
    } catch (err) {
      this._showToast(err.message, "alert")
      btn.disabled = false
      btn.innerHTML = "✨ Audit"
    }
  }

  _pollAudit(runId) {
    const url = this.aiStatusUrlValue.replace("RUN_ID", runId)
    let ticks = 0
    this._poll = setInterval(async () => {
      if (++ticks > 60) { clearInterval(this._poll); return }
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.status === "done") {
        clearInterval(this._poll)
        const flags = (data.result && data.result.flags) || []
        sessionStorage.setItem("builder-finance.flash",
          flags.length ? `Audit found ${flags.length} thing${flags.length > 1 ? "s" : ""} to check` : "Audit clean — nothing flagged")
        window.location.reload()
      } else if (data.status === "failed") {
        clearInterval(this._poll)
        this._showToast(data.error || "The audit didn't finish", "alert")
      }
    }, 2500)
  }

  // ── Shared ──
  _collect(modal, attr) {
    const body = {}
    modal.querySelectorAll(`[${attr}]`).forEach((el) => { body[el.getAttribute(attr)] = el.value })
    return body
  }

  async _postReload(url, body, message, method = "POST") {
    try {
      const resp = await fetch(url, { method, headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Action failed")
      sessionStorage.setItem("builder-finance.flash", message)
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  async resendInvite(e) {
    try {
      const resp = await fetch(e.currentTarget.dataset.url, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't resend the invite")
      this._showToast(data.message || "Invite resent")
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // Both directions confirm first (Phelipe 2026-08-17) — the chip carries
  // the popup copy, so one generic modal serves grant and revoke.
  // ── Edit member access (Phelipe 2026-08-18): one dropdown, three
  // states. Save translates the change into the existing endpoints:
  // grant (invite as member), revoke (back off the team), and the
  // portal toggle (link on/off — off rotates the tokens dead).
  MEMBER_ACCESS_HINTS = {
    full: "They get a real ConsLog account on the project team. Saving sends the invite email.",
    portal: "No account — they work through their secure portal link for pay requests, change orders and documents.",
    none: "The portal link stops working and no invite exists. You can turn access back on anytime."
  }

  openMemberAccess() {
    const modal = this.memberAccessModalTarget
    this.memberAccessSelectTarget.value = modal.dataset.current
    this.memberAccessChanged()
    modal.classList.remove("hidden")
  }

  memberAccessChanged() {
    this.memberAccessHintTarget.textContent = this.MEMBER_ACCESS_HINTS[this.memberAccessSelectTarget.value] || ""
  }

  async saveMemberAccess(e) {
    const modal = this.memberAccessModalTarget
    const current = modal.dataset.current
    const next = this.memberAccessSelectTarget.value
    if (next === current) { this.closeModals(); return }

    const portalOn = modal.dataset.portalEnabled === "true"
    const post = async (url) => {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't update access")
      return data
    }

    const unlock = this._lock("member-access", e)
    if (!unlock) return
    try {
      let message
      if (next === "full") {
        message = (await post(modal.dataset.grantUrl)).message || "ConsLog access granted"
      } else if (current === "full") {
        message = (await post(modal.dataset.revokeUrl)).message || "ConsLog access removed"
        if (next === "portal" && !portalOn) await post(modal.dataset.toggleUrl)
        if (next === "none" && portalOn) await post(modal.dataset.toggleUrl)
        if (next === "none") message = "Access turned off — their links no longer work"
      } else {
        await post(modal.dataset.toggleUrl)
        message = next === "portal" ? "Portal link turned on" : "Access turned off — their links no longer work"
      }
      sessionStorage.setItem("builder-finance.flash", message)
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    } finally {
      unlock()
    }
  }

  openAccessConfirm(e) {
    const d = e.currentTarget.dataset
    this._accessCtx = { url: d.url, pending: d.pending }
    this.accessConfirmTitleTarget.textContent = d.confirmTitle
    this.accessConfirmMsgTarget.textContent = d.confirmMsg
    this.accessConfirmBtnTarget.textContent = d.confirmLabel
    this.accessConfirmModalTarget.classList.remove("hidden")
  }

  async confirmAccessChange() {
    if (!this._accessCtx) return
    this.closeModals()
    await this._postAccess(this._accessCtx.url, this._accessCtx.pending)
  }

  // Fee schedule audit trail (Phelipe 2026-08-17) — every logged touch,
  // newest first.
  async openFeeAudit(e) {
    const url = e.currentTarget.dataset.url
    this.feeAuditListTarget.innerHTML = '<p class="text-[13px] text-gray-400">Loading…</p>'
    this.feeAuditModalTarget.classList.remove("hidden")
    try {
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await resp.json()
      if (!resp.ok) throw new Error("Couldn't load the activity")
      const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
      const rows = data.entries || []
      this.feeAuditListTarget.innerHTML = rows.length
        ? rows.map((r) => `
            <div class="border border-gray-100 rounded-lg px-3 py-2">
              <div class="text-[13px] text-gray-900">${esc(r.message)}</div>
              <div class="text-[13px] text-gray-400">${esc(r.name)}${r.role ? " · " + esc(r.role) : ""} · ${esc(r.at)}</div>
            </div>`).join("")
        : '<p class="text-[13px] text-gray-500">No fee schedule activity yet.</p>'
    } catch (err) {
      this.feeAuditListTarget.innerHTML = `<p class="text-[13px] text-red-600">${err.message}</p>`
    }
  }

  async _postAccess(url, pending) {
    this._showToast(pending)
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "That didn't work — try again")
      sessionStorage.setItem("builder-finance.flash", data.message || "Done")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
